import {DateTime} from "luxon";
import {TRAVEL_OPTION_TYPE} from "../types-enums/activity";

/**
 * Determines whether an array contains at least one element that is neither `undefined` nor `null`.
 *
 * @param {Array<(string | undefined | null)>} arr - The array to check, which may include strings, `undefined`, or `null`.
 * @returns {boolean} True if the array contains at least one element that is not `undefined` or `null`; otherwise, false.
 */
export const isNotUndefinedOrNull = (arr: (string | undefined | null)[]): boolean => arr.some(element => element !== undefined && element !== null)

/**
 * Checks if an array of strings contains at least one non-empty string.
 *
 * @param {string[]} arr - The array of strings to check.
 * @return {boolean} Returns true if the array contains at least one non-empty string, otherwise returns false.
 */
export const someStringsNonEmpty = (arr: string[]): boolean => arr.some(str => str.length > 0);

/**
 * Checks if every string in the provided array is non-empty.
 *
 * This function iterates through the given array of strings and validates
 * that each string has a length greater than 0. If all strings in the array
 * are non-empty, the function returns true; otherwise, it returns false.
 *
 * @param {string[]} arr - The array of strings to be checked.
 * @returns {boolean} Returns true if all strings in the array are non-empty, otherwise false.
 */
export const everyStringsNonEmpty = (arr: string[]): boolean => arr.every(str => str.length > 0);

/**
 * Filters out `undefined` and `null` values from an array of strings, `undefined`, and `null`.
 *
 * @param {Array<(string | undefined | null)>} arr - The array to filter.
 * @returns {string[]} A new array containing only the string values from the input array.
 */
export const filterUndefinedOrNull = (arr: (string | undefined | null)[]): string[] => arr.filter((element): element is string => element !== undefined && element !== null);

/**
 * Generates an array of sequential numbers starting from 1 up to the specified value.
 *
 * @param {number} value - The total number of elements to include in the array. Must be a positive integer.
 * @returns {number[]} An array containing numbers from 1 up to the specified value.
 */
export const createArrayFromNumber = (value: number): number[] => Array.from({length: value}, (_, i) => i + 1)

/**
 * Calculates the number of whole days between two dates.
 *
 * @param {number | DateTime} start - The start date, which can be provided as a timestamp in milliseconds or a DateTime object.
 * @param {number | DateTime} end - The end date, which can be provided as a timestamp in milliseconds or a DateTime object.
 * @returns {number} The total number of whole days between the start and end dates, rounded to the nearest integer.
 */
export const getDaysCountBetweenDates = (start: number | DateTime, end: number | DateTime): number => {
    let startDate: DateTime
    let endDate: DateTime
    if (typeof start === 'number') {
        startDate = DateTime.fromMillis(start)
    } else {
        startDate = start
    }

    if (typeof end === 'number') {
        endDate = DateTime.fromMillis(end)
    } else {
        endDate = end
    }

    return Math.round(endDate.diff(startDate, ["days"]).days)
}

/**
 * Extracts and returns the substring located on the left-hand side of the first colon (:) in the input string.
 * The returned string is trimmed of any leading or trailing whitespace.
 *
 * @param {string} input - The input string to process.
 * @returns {string} - The trimmed substring that appears before the first colon in the input string.
 */
export const getStringLeftSideColon = (input: string): string => input.split(':')[0].trim()

export const DISPLAY_ORDER_TRAVEL_OPTIONS = [TRAVEL_OPTION_TYPE.transport, TRAVEL_OPTION_TYPE.accommodation, TRAVEL_OPTION_TYPE.undertaking]

export const DELAY_TIME_FOR_SPINNER = 250

export const SMALL_SPINNER_SIZE = 20

/**
 * A type guard function that ensures the provided value is neither null nor undefined.
 *
 * @template TValue - The type of the value being checked.
 * @param {TValue | null | undefined} value - The value to check.
 * @returns {value is TValue} Returns true if the value is neither null nor undefined, otherwise false.
 */
export const notEmpty = <TValue>(value: TValue | null | undefined): value is TValue => value !== null && value !== undefined;


/**
 * Trims an array to a specified target length by removing elements
 * starting from the second-to-last position, while preserving the last element.
 *
 * @template T
 * @param {T[]} array - The array to be trimmed.
 * @param {number} targetLength - The desired length of the array.
 * @returns {T[]} A new array trimmed to the specified length, retaining the last element.
 */
export const trimArrayKeepingLast = <T>(array: T[], targetLength: number): T[] => {
    if (array.length <= targetLength) return array
    const result = [...array];
    while (result.length > targetLength) {
        result.splice(result.length - 2, 1);
    }
    return result
}